import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Award, TrendingUp, CheckCircle, Loader2, LogOut, Repeat } from 'lucide-react';
import { useContext } from 'react';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpeakingResults = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { setUser } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Çıkış yapılırken hata oluştu');
    }
  };
  
  useEffect(() => {
    loadSession();
  }, [sessionId]);
  
  const loadSession = async () => {
    try {
      const response = await axios.get(`${API}/speaking/session/${sessionId}`, {
        withCredentials: true
      });
      setSession(response.data);
    } catch (error) {
      toast.error('Sonuçlar yüklenemedi');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Sonuçlar yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  if (!session) return null;
  
  const averageScore = session.answers.reduce((acc, answer) => {
    const scores = answer.evaluation;
    return acc + (scores.fluency_score + scores.grammar_score + scores.content_score) / 3;
  }, 0) / session.answers.length;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Top Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/speaking-practice')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold px-6 py-3 rounded-xl shadow-lg w-full sm:w-auto"
          >
            <Repeat className="w-5 h-5 mr-2" />
            Yeni Pratik Başlat 🎯
          </Button>
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-gray-300 hover:bg-gray-50 font-semibold px-6 py-3 rounded-xl w-full sm:w-auto"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Geri
          </Button>
        </div>
        
        <Card className="glass-effect border-none shadow-xl mb-8">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Award className="w-12 h-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl gradient-text">Konuşma Pratiği Sonuçları</CardTitle>
            <p className="text-gray-600 mt-2">Seviye: {session.level}</p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Ortalama Puan</p>
                <p className="text-4xl font-bold text-blue-600">{averageScore.toFixed(1)}/10</p>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                <CheckCircle className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Tamamlanan Soru</p>
                <p className="text-4xl font-bold text-purple-600">{session.answers.length}</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {session.answers.map((answer, index) => {
                const avgScore = (answer.evaluation.fluency_score + answer.evaluation.grammar_score + answer.evaluation.content_score) / 3;
                return (
                  <Card key={index} className="border border-gray-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Soru {index + 1}</CardTitle>
                      <p className="text-sm text-gray-600 italic">{answer.question}</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Cevabınız:</p>
                        <p className="text-gray-600 italic">"{answer.transcript}"</p>
                      </div>
                      
                      {/* Türkçe Feedback */}
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="text-sm font-semibold text-gray-700 mb-1">🇹🇷 Türkçe:</p>
                        <p className="text-sm">{answer.evaluation.feedback_tr || answer.evaluation.feedback}</p>
                      </div>
                      
                      {/* English Feedback */}
                      <div className="bg-green-50 p-3 rounded">
                        <p className="text-sm font-semibold text-gray-700 mb-1">🇬🇧 English:</p>
                        <p className="text-sm">{answer.evaluation.feedback_en || answer.evaluation.feedback}</p>
                      </div>
                      
                      {/* Corrected Version */}
                      {answer.evaluation.corrected_version && (
                        <div className="bg-purple-50 p-3 rounded">
                          <p className="text-sm font-semibold text-gray-700 mb-1">✨ Geliştirilmiş:</p>
                          <p className="text-sm italic">"{answer.evaluation.corrected_version}"</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-4 gap-3">
                        <div className="text-center p-3 bg-blue-50 rounded">
                          <p className="text-xs text-gray-600">Akıcılık</p>
                          <p className="text-xl font-bold text-blue-600">{answer.evaluation.fluency_score}</p>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded">
                          <p className="text-xs text-gray-600">Dilbilgisi</p>
                          <p className="text-xl font-bold text-purple-600">{answer.evaluation.grammar_score}</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded">
                          <p className="text-xs text-gray-600">İçerik</p>
                          <p className="text-xl font-bold text-green-600">{answer.evaluation.content_score}</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded">
                          <p className="text-xs text-gray-600">Ortalama</p>
                          <p className="text-xl font-bold text-orange-600">{avgScore.toFixed(1)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-center gap-4">
          <Button
            onClick={() => navigate('/exercises')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
          >
            Diğer Egzersizler 🎯
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SpeakingResults;